import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import Overview from '../../../Patient/Reports/components/Overview/Overview';
import Progress from '../../../Patient/Reports/components/Progress/Progress';
import DetailedAnalysis from '../../../Patient/Reports/components/DetailedAnalysis/DetailedAnalysis';
import ReportExtraData from '../../../Patient/ReportExtraData/ReportExtraData';
import '../../../Patient/Reports/styles/Report.css';

// Embedded standard report view for AdminReport. Does not fetch; uses provided data.
// Props:
// - selectedOrderDetails: full order object (same shape as Report uses)
// - displayName: string to show as patient name
const EmbeddedReport = ({ selectedOrderDetails, displayName = 'User' }) => {
  const status = selectedOrderDetails?.db_order?.status_detail?.label;
  const isTestComplete = status === 'Test Complete' || status === 'Order Processing';

  const testHistory = selectedOrderDetails?.db_order?.pdf_documents?.[0]?.parsed_data?.[0]?.data?.testHistory || [];
  const currentTestData = testHistory[testHistory.length - 1] || null;
  const previousTestData = testHistory.length > 1 ? testHistory[testHistory.length - 2] : null;
  const getMetricsFromCategories = useMemo(
    () => (categories) => {
      if (!categories) return [];
      const safeNum = (v) => (Number.isFinite(+v) ? +v : 0);
      const motility = categories?.motility?.metrics || [];
      const morphology = categories?.morphology?.metrics || [];
      const concentration = categories?.concentration?.metrics || [];

      const progressive = safeNum(motility.find((m) => m.name === 'Progressive motility')?.value);
      const overallMotility = safeNum(motility.find((m) => m.name === 'Overall motility')?.value);
      const immotile = safeNum(motility.find((m) => m.name === 'Immotile')?.value);
      const morph = safeNum(morphology.find((m) => m.name === 'Normal forms')?.value);
      const conc = safeNum(concentration.find((m) => m.name === 'Concentration')?.value);
      const funcSpermCount = safeNum(concentration.find((m) => m.name === 'Functional sperm count')?.value);

      const WHO = {
        tmc: 20,
        progressive: 32,
        morph: 4,
        immotile: 20,
        concentration: 15,
        vitality: 58,
      };

      return [
        {
          name: 'TOTAL FUNCTIONAL SPERM(TOTAL MOTILE COUNT)',
          value: funcSpermCount,
          unit: 'million',
          whoValue: WHO.tmc,
          score: Math.min(100, (funcSpermCount / WHO.tmc) * 100),
        },
        {
          name: 'SWIMMING FORWARD(PROGRESSIVE MOTALITY)',
          value: progressive,
          unit: '%',
          whoValue: WHO.progressive,
          score: Math.min(100, (progressive / WHO.progressive) * 100),
        },
        {
          name: 'NORMAL-SHAPED(MORPHOLOGY)',
          value: morph,
          unit: '%',
          whoValue: WHO.morph,
          score: Math.min(100, (morph / WHO.morph) * 100),
        },
        {
          name: 'DNA INTEGRITY(DNA FRAGMENTATION',
          value: immotile,
          unit: '%',
          whoValue: WHO.immotile,
          isInverted: true,
          score: Math.max(0, 100 - (immotile / WHO.immotile) * 100),
        },
        {
          name: 'SPERM COUNT(CONCENTRATION)',
          value: conc,
          unit: 'million/mL',
          whoValue: WHO.concentration,
          score: Math.min(100, (conc / WHO.concentration) * 100),
        },
        {
          name: 'LIVING SPERM(VITALITY)',
          value: overallMotility,
          unit: '%',
          whoValue: WHO.vitality,
          score: Math.min(100, (overallMotility / WHO.vitality) * 100),
        },
      ];
    },
    []
  );

  const currentTest = useMemo(() => {
    if (!currentTestData) return null;
    const scoreInfo = selectedOrderDetails?.db_order?.swimscore_overall_score;
    return {
      date: currentTestData.date,
      overallScore: scoreInfo,
      rawSwimScore: scoreInfo,
      trend: null,
      keyMetrics: getMetricsFromCategories(currentTestData?.categories),
    };
  }, [currentTestData, selectedOrderDetails, getMetricsFromCategories]);

  const userData = currentTestData
    ? {
        name: displayName,
        testHistory: [currentTestData],
      }
    : { testHistory: [] };

  return (
    <div className="report-container">
      <h1 className="report-title">Patient Report {displayName}</h1>

      {/* Order Info */}
      {selectedOrderDetails && (
        <div className="order-info">
          <p>
            <strong>Order Number:</strong> #{selectedOrderDetails.order_number}
          </p>
          <p>
            <strong>Status:</strong> {status || 'N/A'}
          </p>
        </div>
      )}

      {/* Display scoring */}
      {isTestComplete && currentTest ? (
        <div className="overview-section">
          <Overview currentTest={currentTest} previousTest={previousTestData} userName={displayName} />

          <div className="progress-section">
            <Progress userData={userData} />
          </div>

          <DetailedAnalysis userData={userData} />
          <ReportExtraData userData={userData} />
        </div>
      ) : (
        <p className="no-data">Your test data is still processing...</p>
      )}
    </div>
  );
};

EmbeddedReport.propTypes = {
  selectedOrderDetails: PropTypes.shape({
    order_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    db_order: PropTypes.shape({
      status_detail: PropTypes.shape({
        label: PropTypes.string,
      }),
      pdf_documents: PropTypes.arrayOf(
        PropTypes.shape({
          parsed_data: PropTypes.arrayOf(
            PropTypes.shape({
              data: PropTypes.shape({
                testHistory: PropTypes.array,
              }),
            })
          ),
          presigned_url: PropTypes.string,
          title: PropTypes.string,
        })
      ),
      swimscore_overall_score: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }).isRequired,
  displayName: PropTypes.string,
};

export default EmbeddedReport;
